import React, { useMemo } from 'react';
import type { GanttData } from '../types';
import { ROW_HEIGHT } from '../constants';
import { diffDays } from '../dateUtils';
import { useGanttContext } from '../GanttContext';

type Props = {
  data: GanttData;
  activeDrag: {
    taskId: string;
    side: 'start' | 'end';
    currentX: number;
    currentY: number;
  } | null;
};

// Функция отрисовки красивой кривой Безье
const getPath = (x1: number, y1: number, x2: number, y2: number): string => {
  const curve = 40; // сила изгиба
  return `M ${x1} ${y1} C ${x1 - curve} ${y1}, ${x1 - curve} ${y2}, ${x1} ${y2} L ${x2} ${y2}`; 
  // Упрощенная S-образная кривая: M start -> Bezier -> End
  // Для Gantt часто используют: Start -> (horizontal) -> Corner -> (vertical) -> Corner -> End.
  // Ниже вариант для простых дуг:
  
  // const halfX = (x2 - x1) / 2;
  // // Если точка назначения левее, делаем петлю вниз
  // if (x2 < x1) {
  //    return `M ${x1} ${y1} 
  //            C ${x1 + curve} ${y1}, ${x1 + curve} ${y1 + 15}, ${x1} ${y1 + 15}
  //            L ${x2} ${y1 + 15}
  //            C ${x2 - curve} ${y1 + 15}, ${x2 - curve} ${y2}, ${x2} ${y2}`;
  // }
  
  // return `M ${x1} ${y1} C ${x1 + curve} ${y1}, ${x2 - curve} ${y2}, ${x2} ${y2}`;
};

export const DependencyLayer: React.FC<Props> = ({ data, activeDrag }) => {
  const { timelineStart, columnWidth } = useGanttContext();

  // 1. Кэшируем координаты задач
  const taskCoords = useMemo(() => {
    const map = new Map<string, { xStart: number; xEnd: number; y: number }>();
    const taskMap = new Map(data.tasks.map(t => [t.id, t]));

    data.rows.forEach((row, rowIndex) => {
      const y = rowIndex * ROW_HEIGHT + ROW_HEIGHT / 2;
      row.taskIds.forEach(tid => {
        const task = taskMap.get(tid);
        if (task) {
          const daysStart = diffDays(timelineStart, task.startDate);
          const daysEnd = diffDays(timelineStart, task.endDate);
          const xStart = Math.max(0, daysStart * columnWidth);
          const xEnd = Math.max(0, daysEnd * columnWidth);
          map.set(tid, { xStart, xEnd, y });
        }
      });
    });
    return map;
  }, [data.rows, data.tasks, timelineStart, columnWidth]);

  return (
    <svg
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none', // чтобы клики проходили сквозь линии
        overflow: 'visible',
      }}
    >
      <defs>
        <marker id="arrow" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
          <path d="M0,0 L0,6 L6,3 z" fill="#888" />
        </marker>
      </defs>

      {/* Рисуем существующие связи */}
      {data.dependencies.map(dep => {
        const from = taskCoords.get(dep.fromTaskId);
        const to = taskCoords.get(dep.toTaskId);
        if (!from || !to) return null;

        const x1 = dep.type.startsWith('S') ? from.xStart : from.xEnd;
        const y1 = from.y;
        const x2 = dep.type.endsWith('S') ? to.xStart : to.xEnd;
        const y2 = to.y;

        return (
          <path
            key={dep.id}
            d={getPath(x1, y1, x2, y2)}
            fill="none"
            stroke="#999"
            strokeWidth="1.5"
            markerEnd="url(#arrow)"
          />
        );
      })}

      {/* Рисуем "призрачную" линию при создании */}
      {activeDrag && taskCoords.get(activeDrag.taskId) && (
        <path
          d={getPath(
            activeDrag.side === 'start'
              ? taskCoords.get(activeDrag.taskId)!.xStart
              : taskCoords.get(activeDrag.taskId)!.xEnd,
            taskCoords.get(activeDrag.taskId)!.y,
            activeDrag.currentX,
            activeDrag.currentY
          )}
          fill="none"
          stroke="#3b82f6"
          strokeWidth="2"
          strokeDasharray="4"
        />
      )}
    </svg>
  );
};
